import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from './auth/AuthGuard';
import { authRoles } from './auth/authRoles';
import Loadable from './components/Loadable';
import MatxLayout from './components/MatxLayout/MatxLayout';
import materialRoutes from 'app/views/material-kit/MaterialRoutes';
import { allowAction } from 'app/utils/utils';
import { RequireHub, RequirePatient } from 'app/components/RouteRequirements';


// dashboard page
const Dashboard = Loadable(lazy(() => import('app/views/dashboard/Dashboard')));
const PatientDashboard = Loadable(lazy(() => import('app/views/dashboard/PatientDashboard')));
const ActionDashboard = Loadable(lazy(() => import('app/views/dashboard/ActionDashboard')));
const ReportDashboard = Loadable(lazy(() => import('app/views/dashboard/ReportDashboard.tsx')));
const WatchDashboard = Loadable(lazy(() => import('app/views/dashboard/WatchDashboard')));
const HcbPawDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawDashboard')));
const HcbPawLoginLinkDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawLoginLinkDashboard')));
const HcbPawLinkLogsDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawLinkLogsDashboard')));
const HcbPawAppLinksDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawAppLinksDashboard')));
const HcbPawTelehealthDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawTelehealthDashboard')));
const HcbPawAnnouncementsDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawAnnouncementsDashboard')));
const HcbPawSmsAnnouncementsDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawSmsAnnouncementsDashboard')));
const HcbPawSurveysDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawSurveysDashboard')));
const HcbPawSurveyResponsesDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawSurveyResponsesDashboard')));
const HcbPawQuestionnairesDashboard = Loadable(lazy(() => import('app/views/dashboard/HcbPawQuestionnairesDashboard')));
const AppConfigDashboard = Loadable(lazy(() => import('app/views/dashboard/AppConfigDashboard')));
const LlmTestDashboard = Loadable(lazy(() => import('app/views/dashboard/LlmTestDashboard')));
const FhirEncountersDashboard = Loadable(lazy(() => import('app/views/fhir/FhirEncountersDashboard')));
const FhirAllergiesDashboard = Loadable(lazy(() => import('app/views/fhir/FhirAllergiesDashboard')));
const FhirDiagnosticReportsDashboard = Loadable(lazy(() => import('app/views/fhir/FhirDiagnosticReportsDashboard')));
const FhirImmunizationsDashboard = Loadable(lazy(() => import('app/views/fhir/FhirImmunizationsDashboard')));
const FhirMedicationDispensesDashboard = Loadable(lazy(() => import('app/views/fhir/FhirMedicationDispensesDashboard')));
const FhirMedicationRequestsDashboard = Loadable(lazy(() => import('app/views/fhir/FhirMedicationRequestsDashboard')));
const FhirMedicationStatementsDashboard = Loadable(lazy(() => import('app/views/fhir/FhirMedicationStatementsDashboard')));
//const OldHubLogging = Loadable(lazy(() => import('app/views/dashboard/HubLogging'))));
const HubLogging = Loadable(lazy(() => import('app/HubLogging/LoggingTable.tsx')));
const MQTTLogging = Loadable(lazy(() => import('app/MQTTLogging/MQTTLogging.tsx')));
const EducationDashboard = Loadable(lazy(() => import('app/views/dashboard/EducationDashboard')));
const CompanionDashboard = Loadable(lazy(() => import('app/views/dashboard/CompanionDashboard')));
const SmsDashboard = Loadable(lazy(() => import('app/views/dashboard/SmsDashboard')));
const SupportDashboard = Loadable(lazy(() => import('app/views/dashboard/SupportDashboard')));
const ChatDashboard = Loadable(lazy(() => import('app/views/dashboard/ChatDashboard')));

const SmsMessagesDashboard = Loadable(lazy(() => import('app/views/dashboard/SmsMessagesDashboard')));
const SmsOptStateDashboard = Loadable(lazy(() => import('app/views/dashboard/SmsOptStateDashboard')));

const Query_soracom_sim = Loadable(lazy(() => import('app/views/query/Query_soracom_sim')));

const HCBotAppConfig = Loadable(lazy(() => import('app/views/hcbot/AppConfig')));
const HCBotActivity = Loadable(lazy(() => import('app/views/hcbot/Activity')));
const HCBotChatLog = Loadable(lazy(() => import('app/views/hcbot/ChatLog')));
const HCBotGuardrails = Loadable(lazy(() => import('app/views/hcbot/Guardrails')));
const HCBotPatientMemory = Loadable(lazy(() => import('app/views/hcbot/PatientMemory')));
const HCBotPhonePatientLookup = Loadable(lazy(() => import('app/views/hcbot/PhonePatientLookup')));


// demo - session pages
const NotFound = Loadable(lazy(() => import('app/views/sessions/NotFound')));
const JwtLogin = Loadable(lazy(() => import('app/views/sessions/JwtLogin')));
const JwtRegister = Loadable(lazy(() => import('app/views/sessions/JwtRegister')));
const ForgotPassword = Loadable(lazy(() => import('app/views/sessions/ForgotPassword')));

const routes = [
  {
    element: (
      <AuthGuard>
        <MatxLayout />
      </AuthGuard>
    ),
    children: [
      ...materialRoutes,

      // dashboard route
      {
        path: '/dashboard/hub',
        element: (
          <RequireHub>
            <Dashboard />
          </RequireHub>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/patient',
        element: (
          <RequirePatient>
            <PatientDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw',
        element: <Navigate to="/dashboard/hcbpaw/login_link" />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir',
        element: <Navigate to="/dashboard/fhir/encounters" />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/login_link',
        element: (
          <RequirePatient>
            <HcbPawLoginLinkDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/link_logs',
        element: (
          <RequirePatient>
            <HcbPawLinkLogsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/app_links',
        element: (
          <RequirePatient>
            <HcbPawAppLinksDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/telehealth',
        element: (
          <RequirePatient>
            <HcbPawTelehealthDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/announcements',
        element: (
          <RequirePatient>
            <HcbPawAnnouncementsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/sms-announcements',
        element: (
          <RequirePatient>
            <HcbPawSmsAnnouncementsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/surveys',
        element: (
          <RequirePatient>
            <HcbPawSurveysDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/surveys/responses',
        element: (
          <RequirePatient>
            <HcbPawSurveyResponsesDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hcbpaw/questionnaires',
        element: (
          <RequirePatient>
            <HcbPawQuestionnairesDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/encounters',
        element: (
          <RequirePatient>
            <FhirEncountersDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/allergies',
        element: (
          <RequirePatient>
            <FhirAllergiesDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/diagnostic-reports',
        element: (
          <RequirePatient>
            <FhirDiagnosticReportsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/vaccines',
        element: (
          <RequirePatient>
            <FhirImmunizationsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/medication-dispenses',
        element: (
          <RequirePatient>
            <FhirMedicationDispensesDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/medication-requests',
        element: (
          <RequirePatient>
            <FhirMedicationRequestsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/fhir/medication-statements',
        element: (
          <RequirePatient>
            <FhirMedicationStatementsDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/app_config',
        element: (
          <RequirePatient>
            <AppConfigDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/llmtest',
        element: <LlmTestDashboard />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/hubs',
        element: <Dashboard />,
        auth: authRoles.super_admin
      },

      {
        path: '/dashboard/watch',
        element: (
          <RequirePatient>
            <WatchDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/dashboard/logging',
        element: (
          <RequireHub>
            <HubLogging />
          </RequireHub>
        ),
        /* element: <HubLogging hubId="LM8HA1B3YW3"/>, */
        auth: authRoles.admin
      },

      {
        path: '/dashboard/mqtt',
        element: (
          <RequireHub>
            <MQTTLogging />
          </RequireHub>
        ),
        /* element: <HubLogging hubId="LM8HA1B3YW3"/>, */
        auth: authRoles.admin
      },

      {
        path: '/dashboard/reports',
        element: <Dashboard />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/query/soracom_sim',
        element: <Query_soracom_sim />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/education',
        element: <EducationDashboard />,
        auth: authRoles.admin
      },
            
      {
        path: '/dashboard/companion',
        element: <CompanionDashboard />,
        auth: authRoles.admin
      },
            
      {
        path: '/dashboard/sms',
        element: <SmsDashboard />,
        auth: authRoles.admin
      },
            
      {
        path: '/dashboard/support',
        element: <SupportDashboard />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/shortcodes/sms-messages',
        element: <SmsMessagesDashboard />,
        auth: authRoles.admin
      },

      {
        path: '/dashboard/shortcodes/sms-opt-state',
        element: <SmsOptStateDashboard />,
        auth: authRoles.admin
      },
            
      {
        path: '/dashboard/chat',
        element: (
          <RequirePatient>
            <ChatDashboard />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      // HCBot routes
      {
        path: '/hcbot/app_config',
        element: <HCBotAppConfig />,
        auth: authRoles.admin
      },

      {
        path: '/hcbot/activity',
        element: <HCBotActivity />,
        auth: authRoles.admin
      },
      {
        path: '/hcbot/chat_log',
        element: <HCBotChatLog />,
        auth: authRoles.admin
      },
      {
        path: '/hcbot/guardrails',
        element: <HCBotGuardrails />,
        auth: authRoles.admin
      },
      {
        path: '/hcbot/patient_memory',
        element: (
          <RequirePatient>
            <HCBotPatientMemory />
          </RequirePatient>
        ),
        auth: authRoles.admin
      },

      {
        path: '/hcbot/phone_lookup',
        element: <HCBotPhonePatientLookup />,
        auth: authRoles.admin
      },

    ]
  },

  // session pages route
  { path: '/session/404', element: <NotFound /> },
  { path: '/session/signin', element: <JwtLogin /> },
  { path: '/session/signup', element: <JwtRegister /> },
  { path: '/session/forgot-password', element: <ForgotPassword /> },

  { path: '/', element: <Navigate to="dashboard/hub" /> },
  { path: '*', element: <NotFound /> }
];


export function GenRoutes(actions,reports) {
  try {
    if (actions) {
      console.log(actions);
      console.log(routes);
      console.log(routes[0]);
      console.log(routes[0].children);

      let actionItems = [
        {
          path: '/action/xBLE_SCAN_linux',
          element: <Dashboard command={'xBLE_SCAN_linux'} />,
          auth: authRoles.admin
        },
        {
          path: '/dashboard/reports',
          element: <Dashboard />,
          auth: authRoles.admin
        },
        {
          path: '/dashboard/reports',
          element: <Dashboard />,
          auth: authRoles.admin
        },
      ];

      console.log(actionItems);
      routes[0].children = routes[0].children.concat(actionItems);

      for (let action_idx1 = 0; action_idx1 < actions.length; action_idx1++) {
        let action = actions[action_idx1];
        //always load the route - if (allowAction(action)) 
        {

          let actionItem = [
            {
              path: '/action/'+action.action_name,
              element: (
                <RequireHub>
                  <ActionDashboard action={action} />
                </RequireHub>
              ),
              auth: authRoles.admin
            },
          ];
    
          routes[0].children = routes[0].children.concat(actionItem);
        }
      }


      console.log(routes[0].children);

      /*
      for (let i = 0; i < routes[0].children.length; i++) {
        let child = routes[0].children[i];
        if ( child.path == '/dashboard/logging' ) {
          let newItems = []
          routes[0].children.splice(i,0,newItems);
          break;
        }
      }
      */

    }
    if (reports) {
      console.log(reports);

      let reportItems = [
        {
          path: '/dashboard/reports',
          element: <ReportDashboard />,
          auth: authRoles.admin
        }
      ];
      routes[0].children = routes[0].children.concat(reportItems);

      for (let report_idx1 = 0; report_idx1 < reports.length; report_idx1++) {
        let report = reports[report_idx1];
        //always load the route - if (allowAction(action)) 
        {

          let reportItem = [
            {
              path: '/report/'+report.report_name,
              element: (
                <RequireHub>
                  <ReportDashboard report={report} />
                </RequireHub>
              ),
              auth: authRoles.admin
            },
          ];
    
          routes[0].children = routes[0].children.concat(reportItem);
        }
      }
      console.log(routes[0].children);
    }
  } catch (e) { }
  console.log(routes);
  return routes;
}

//export default routes;
export default GenRoutes;
